// Download Center — run Phase 5 and download the final corporate package
// (plan §M4.7).
//
// Artifacts are curated for a **non-technical corporate audience**:
// only PDF, HTML, DOCX, XLSX, PNG, and the signed ZIP bundles are
// surfaced as standalone tiles. Raw ``.md`` / ``.json`` / ``.yaml`` /
// orphan ``.svg`` intermediates still ship inside ``export.zip`` for
// engineering / audit consumption — but they are never advertised as
// a standalone download here.
//
// All download links hit FastAPI FileResponse endpoints so the
// browser streams them with `attachment` disposition.
//
// UX v0.7 (plan §P1.7) — the legacy shadcn ``<Card>`` shell has been
// migrated to the canonical ``alm-*`` design language so the surface
// matches the rest of the focus pane. The download tiles still use a
// custom layout (anchor + grid) because the audit explicitly calls
// out that they need their own treatment.

import { useMutation } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileDown,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  Globe2,
  PackageOpen,
  Presentation,
  ShieldCheck,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useState } from 'react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { PrimaryButton, Section } from '@/components/ui/shell'
import { SkeletonRow } from '@/components/ui/skeleton'
import { api, ApiError, projectFileUrl } from '@/lib/api'
import { waitForPhaseCompletion } from '@/lib/phaseEvents'
import { humanBytes } from '@/lib/format'


interface ExportResult {
  template_name: string
  misfit_count: number
  rendered_diagrams: string[]
  misfits: Array<{
    kind: string
    section_id: string | null
    section_heading: string | null
    target_content: string | null
    message: string
  }>
  total_bytes: number
  artifacts: Record<string, string>
}


export function DownloadCenter({
  projectId,
  runId,
}: {
  projectId: string
  runId: number
}) {
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ExportResult | null>(null)

  const run = useMutation({
    mutationFn: async () => {
      await api.runPhase5(projectId, runId)
      // Backend's ``_phase_5_body`` stamps the full ExportResult shape
      // onto the ``phase_completed`` event payload so the panel can
      // hydrate without a follow-up GET. See phases.py.
      return waitForPhaseCompletion(projectId, runId, 5)
    },
    onMutate: () => {
      setError(null)
    },
    onSuccess: (event) => {
      const payload = (event?.payload ?? {}) as Partial<ExportResult>
      setResult({
        template_name: String(payload.template_name ?? ''),
        misfit_count: Number(payload.misfit_count ?? 0),
        rendered_diagrams: Array.isArray(payload.rendered_diagrams)
          ? (payload.rendered_diagrams as string[])
          : [],
        misfits: Array.isArray(payload.misfits)
          ? (payload.misfits as ExportResult['misfits'])
          : [],
        total_bytes: Number(payload.total_bytes ?? 0),
        artifacts:
          payload.artifacts && typeof payload.artifacts === 'object'
            ? (payload.artifacts as Record<string, string>)
            : {},
      })
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError(String(err))
      }
    },
  })

  const runStr = String(runId).padStart(3, '0')
  const bundleUrl = `/api/projects/${encodeURIComponent(
    projectId,
  )}/runs/${runId}/downloads/export.zip`
  const auditUrl = `/api/projects/${encodeURIComponent(
    projectId,
  )}/runs/${runId}/downloads/audit-package.zip`

  // The backend exposes every corporate artifact in ``artifacts``. We
  // tile them in priority order: HTML → PDF → DOCX → XLSX → Bundles →
  // Audit. PNG diagrams are linked inline under a separate strip.
  const artifacts = result?.artifacts ?? {}

  return (
    <div className="alm-fadein">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-info">
              <PackageOpen className="h-3 w-3" /> Phase 5 · Exporter
            </span>
            <span className="alm-chip">Run #{runId}</span>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">
            Download Center
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Run the deterministic Exporter and download the final
            corporate-ready requirements package — PDF, HTML, Word, Excel,
            and signed ZIP bundles (plan §M4.7). Engineering-only raw
            artifacts (markdown, JSON, YAML) remain inside{' '}
            <code className="font-mono text-slate-300">export.zip</code>.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <PrimaryButton
          onClick={() => run.mutate()}
          loading={run.isPending}
          icon={<FileDown className="h-4 w-4" aria-hidden />}
        >
          {result ? 'Re-run Exporter' : 'Run Exporter'}
        </PrimaryButton>
        {result ? (
          <div className="text-xs text-slate-400 space-y-0.5">
            <div>
              <span className="font-semibold text-slate-300">Template:</span>{' '}
              {result.template_name}
            </div>
            <div>
              <span className="font-semibold text-slate-300">Bundle size:</span>{' '}
              {humanBytes(result.total_bytes)}
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" aria-hidden />
          <AlertTitle>Exporter failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {result ? (
        <>
          {result.misfit_count > 0 ? (
            <Alert variant="warning" className="mb-4">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              <AlertTitle>
                {result.misfit_count} template mis-fit
                {result.misfit_count === 1 ? '' : 's'}
              </AlertTitle>
              <AlertDescription>
                <ul className="mt-2 space-y-1 text-xs">
                  {result.misfits.slice(0, 5).map((m, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="alm-chip text-[10px] uppercase">
                        {m.kind.replace(/_/g, ' ')}
                      </span>
                      <span>
                        {m.section_heading ? (
                          <strong>{m.section_heading}: </strong>
                        ) : null}
                        {m.message}
                      </span>
                    </li>
                  ))}
                  {result.misfits.length > 5 ? (
                    <li className="text-[10px] text-slate-400">
                      …and {result.misfits.length - 5} more. See G5 for the
                      full mis-fit escalation flow.
                    </li>
                  ) : null}
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="success" className="mb-4">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              <AlertTitle>Export ready</AlertTitle>
              <AlertDescription>
                All template sections resolved cleanly.
              </AlertDescription>
            </Alert>
          )}
        </>
      ) : null}

      {/* Primary corporate deliverables */}
      <Section label="Corporate deliverables" className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Audit §8.5 / plan §P2.20 — when the Exporter is running,
              show skeleton tiles where the corporate deliverables will
              land so the user has visual confirmation that work is in
              progress (instead of an empty grid + tiny spinner). */}
          {run.isPending && !result ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : null}
          {artifacts.requirements_pdf ? (
            <DownloadCard
              testKey="requirements-pdf"
              title="Requirements (PDF)"
              subtitle="Print-ready, paginated, cover page included"
              badge="Recommended"
              icon={FileType}
              href={projectFileUrl(projectId, artifacts.requirements_pdf)}
              filename={`${projectId}-run-${runStr}-requirements.pdf`}
            />
          ) : null}
          {artifacts.requirements_html ? (
            <DownloadCard
              testKey="requirements-html"
              title="Requirements (HTML)"
              subtitle="Self-contained, styled, embedded diagrams"
              icon={Globe2}
              href={projectFileUrl(projectId, artifacts.requirements_html)}
              filename={`${projectId}-run-${runStr}-requirements.html`}
            />
          ) : null}
          {artifacts.requirements_docx ? (
            <DownloadCard
              testKey="requirements-docx"
              title="Requirements (Word)"
              subtitle="Editable .docx with styles and tables"
              icon={FileText}
              href={projectFileUrl(projectId, artifacts.requirements_docx)}
              filename={`${projectId}-run-${runStr}-requirements.docx`}
            />
          ) : null}
          {artifacts.traceability_xlsx ? (
            <DownloadCard
              testKey="traceability-xlsx"
              title="Traceability Matrix (Excel)"
              subtitle="Filterable workbook: BR/SR ↔ Charter / Constraints"
              icon={FileSpreadsheet}
              href={projectFileUrl(projectId, artifacts.traceability_xlsx)}
              filename={`${projectId}-run-${runStr}-traceability.xlsx`}
            />
          ) : null}
          {artifacts.requirements_pptx ? (
            <DownloadCard
              testKey="requirements-pptx"
              title="Executive Briefing (PowerPoint)"
              subtitle="Slide deck with cover, summary, stats, and diagrams"
              icon={Presentation}
              href={projectFileUrl(projectId, artifacts.requirements_pptx)}
              filename={`${projectId}-run-${runStr}-briefing.pptx`}
            />
          ) : null}
          {/* When the Exporter hasn't run yet, the corporate-deliverable
              section is empty. Show a soft empty-state hint so the panel
              never collapses to a confusing blank slab. */}
          {!result ? (
            <p className="md:col-span-2 text-xs text-slate-500">
              Run the Exporter to populate the corporate deliverables.
            </p>
          ) : null}
        </div>
      </Section>

      {/* Signed bundles */}
      <Section label="Signed bundles" className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DownloadCard
            testKey="export-zip"
            title="Full Package (ZIP)"
            subtitle="All corporate deliverables + engineering sources"
            icon={PackageOpen}
            filename={`${projectId}-run-${runStr}-export.zip`}
            href={bundleUrl}
            available={!!result || undefined}
          />
          <DownloadCard
            testKey="audit-package-zip"
            title="Audit Evidence (ZIP)"
            subtitle="Hash-chained history + provenance for compliance"
            icon={ShieldCheck}
            filename={`${projectId}-run-${runStr}-audit-package.zip`}
            href={auditUrl}
            available={!!result || undefined}
          />
        </div>
      </Section>

      {/* Diagram PNGs — only surface if any exist */}
      {result && result.rendered_diagrams.length > 0 ? (
        <div className="alm-card mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <FileImage className="h-3.5 w-3.5" aria-hidden />
              Diagrams (PNG)
            </div>
            <div className="text-[10px] text-slate-500">
              {result.rendered_diagrams.length} rendered
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {result.rendered_diagrams.map((rel) => {
              // The backend emits PNG siblings alongside each SVG
              // (same stem). We link to the PNG form because it is
              // the corporate-friendly rendition.
              const png = rel.replace(/\.svg$/i, '.png')
              const name = png.split('/').pop() ?? png
              return (
                <a
                  key={rel}
                  href={projectFileUrl(projectId, png)}
                  download={name}
                  className="inline-flex items-center gap-1.5 rounded border border-slate-700/60 bg-slate-900/40 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800/60 transition"
                >
                  <FileImage className="h-3 w-3" aria-hidden />
                  {name}
                </a>
              )
            })}
          </div>
        </div>
      ) : null}

      <p className="text-[10px] text-slate-500">
        All downloads are streamed as file attachments. The audit package
        hash-chain is verified by{' '}
        <code className="font-mono">ai_alm audit verify &lt;project_id&gt;</code>.
        Raw Markdown, JSON, and YAML sources are kept inside{' '}
        <code className="font-mono">export.zip</code> — they are not intended
        for non-technical reviewers.
      </p>
    </div>
  )
}


function DownloadCard({
  testKey,
  title,
  subtitle,
  href,
  filename,
  available,
  icon: Icon,
  badge,
}: {
  /**
   * E2E test hook key (T2 — plan §5.5 / check-testids.mjs). Renders
   * as ``data-testid={`download-${testKey}`}`` on the anchor so a
   * Playwright spec can target a specific deliverable without text
   * matching. Optional so non-E2E call sites stay terse.
   */
  testKey?: string
  title: string
  subtitle: string
  href: string
  filename?: string
  available?: boolean
  icon?: LucideIcon
  badge?: string
}) {
  const isDisabled = available === false
  return (
    <a
      data-testid={testKey ? `download-${testKey}` : undefined}
      href={isDisabled ? undefined : href}
      download={filename}
      className={
        'group flex items-center justify-between gap-3 rounded-md border border-slate-700/60 bg-slate-900/40 p-3 transition hover:bg-slate-800/40' +
        (isDisabled ? ' opacity-50 pointer-events-none' : '')
      }
      aria-disabled={isDisabled}
      onClick={(e) => {
        if (isDisabled) e.preventDefault()
      }}
    >
      <div className="flex items-start gap-3 min-w-0">
        {Icon ? (
          <Icon
            className="h-5 w-5 text-slate-400 shrink-0 mt-0.5"
            aria-hidden
          />
        ) : null}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-slate-100 truncate">
              {title}
            </div>
            {badge ? (
              <span className="alm-chip alm-chip-info text-[9px] uppercase">
                {badge}
              </span>
            ) : null}
          </div>
          <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>
        </div>
      </div>
      <Download
        className="h-4 w-4 text-slate-500 group-hover:text-slate-200 shrink-0"
        aria-hidden
      />
    </a>
  )
}
